import { useEffect, useState } from "react";
import { Check, ChevronsUpDown, Globe2 } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { api } from "@/lib/api";

let cache;
export const loadCountries = () => (cache ||= api.get("/countries").then((r) => r.data).catch((e) => { cache = null; throw e; }));

export function useCountries() {
  const [list, setList] = useState([]);
  useEffect(() => { loadCountries().then(setList).catch(() => {}); }, []);
  return list;
}

export function Flag({ iso, className = "" }) {
  if (!iso || iso.length !== 2) return <Globe2 className={`h-4 w-4 text-slate-400 ${className}`} />;
  return <span className={`text-base leading-none ${className}`} aria-hidden>{String.fromCodePoint(...[...iso.toUpperCase()].map((c) => 127397 + c.charCodeAt(0)))}</span>;
}

export function CountryPicker({ value, onChange, countries, testid = "country" }) {
  const [open, setOpen] = useState(false);
  const cur = countries.find((c) => c.code === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid={`${testid}-picker`} className="flex h-11 w-full items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 text-left text-sm font-semibold text-slate-900 outline-none hover:border-slate-400 focus:border-slate-900">
          <Flag iso={cur?.code} />
          <span className="flex-1 truncate" data-testid={`${testid}-picker-value`}>{cur?.name || "Pick a country"}</span>
          {cur && <span className="font-mono text-xs text-slate-500">{cur.currency}</span>}
          <ChevronsUpDown className="h-3.5 w-3.5 text-slate-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[min(92vw,22rem)] p-0" align="start" data-testid={`${testid}-list`}>
        <Command>
          <CommandInput placeholder="Search country or currency" data-testid={`${testid}-search`} />
          <CommandList className="max-h-72">
            <CommandEmpty>No country found</CommandEmpty>
            <CommandGroup>
              {countries.map((c) => (
                <CommandItem key={c.code} value={`${c.name} ${c.code} ${c.currency}`} onSelect={() => { onChange(c.code); setOpen(false); }} data-testid={`${testid}-option-${c.code}`}>
                  <Flag iso={c.code === "OTHER" ? null : c.code} className="mr-1" />
                  <span className="flex-1 truncate">{c.name}</span>
                  <span className="font-mono text-[11px] text-slate-500">{c.currency} · {c.tax_label === "None" ? "no tax" : `${c.tax_rate}%`}</span>
                  {c.code === value && <Check className="ml-1 h-3.5 w-3.5 text-emerald-600" />}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export function RegionSelect({ country, value, onChange, testid = "region" }) {
  if (!country?.regions) return null;
  const word = country.code === "US" ? "State" : "Province / territory";
  return (
    <label className="block space-y-1.5">
      <span className="text-xs font-bold uppercase tracking-wider text-slate-600">{word}</span>
      <select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-select`} className="h-11 w-full rounded-xl border border-slate-200 bg-white px-3 text-sm font-semibold text-slate-900 outline-none focus:border-slate-900">
        {country.regions.map((r) => (
          <option key={r.code} value={r.code}>{`${r.name} — ${r.tax_label} ${r.tax_rate}%`}</option>
        ))}
      </select>
    </label>
  );
}

// Country (+ region) → {currency, decimals, tax_label, tax_rate}
export function taxOf(country, region) {
  if (!country) return null;
  const r = country.regions?.find((x) => x.code === region) || country.regions?.[0];
  return { ...country, region: r?.code || null, tax_label: r ? r.tax_label : country.tax_label, tax_rate: r ? r.tax_rate : country.tax_rate };
}
